import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LANGUAGES, DEFAULT_LANG, findLanguage } from './languages.js'
import { getTranslationManager } from './services/TranslationManager.js'
import { PRECOMPUTED_LANGS } from './precomputed-langs.js'

// Only show languages whose translations are bundled at build time
// (i.e. they switch instantly with zero network). EN is always
// included as the source language. We use the lightweight code list
// (not the ~945 KB JSON) so this component adds nothing to the bundle.
const INSTANT_LANGS = new Set(['en', ...PRECOMPUTED_LANGS])
const VISIBLE_LANGUAGES = LANGUAGES.filter((l) => INSTANT_LANGS.has(l.code))

/**
 * Replaces every `.lang-toggle` chip rendered by the source HTML
 * (each SPA sub-page footer has one) with a multi-language dropdown
 * driven by the singleton TranslationManager. Language choice
 * persists for the session.
 */
export default function FooterLanguageSwitcher() {
  const [hosts, setHosts] = useState([])
  const [{ lang, busy, progress }, setState] = useState(() => getTranslationManager().getState())
  const [openHost, setOpenHost] = useState(null)
  const managerRef = useRef(null)

  // -------- Start the singleton + subscribe to its state --------------
  useEffect(() => {
    const mgr = getTranslationManager()
    managerRef.current = mgr
    mgr.start()
    setState(mgr.getState())
    const unsub = mgr.subscribe((s) => setState(s))
    return () => unsub()
  }, [])

  // -------- Find/replace every `.lang-toggle` chip --------------------
  useEffect(() => {
    function scan() {
      const found = Array.from(document.querySelectorAll('.lang-toggle'))
      found.forEach((el) => {
        if (!el.classList.contains('pb-lang-managed')) {
          el.classList.add('pb-lang-managed')
          el.setAttribute('data-pb-skip-translate', '')
          el.innerHTML = ''
        }
      })
      setHosts((prev) => {
        if (prev.length === found.length && prev.every((p, i) => p === found[i])) return prev
        return found
      })
    }
    scan()
    const mo = new MutationObserver(scan)
    mo.observe(document.body, { childList: true, subtree: true })
    return () => mo.disconnect()
  }, [])

  const handlePick = useCallback((code) => {
    setOpenHost(null)
    const mgr = managerRef.current
    if (mgr) mgr.setLanguage(code)
  }, [])

  return (
    <>
      {hosts.map((host, i) =>
        createPortal(
          <Pill
            current={lang}
            busy={busy}
            progress={progress}
            open={openHost === host}
            onToggle={() => setOpenHost((cur) => (cur === host ? null : host))}
            onPick={handlePick}
            onClose={() => setOpenHost(null)}
          />,
          host,
          'pb-lang-' + i
        )
      )}
      <style>{globalStyles}</style>
    </>
  )
}

// ----- Pill UI (rendered into each .lang-toggle via portal) -----------
function Pill({ current, busy, progress, open, onToggle, onPick, onClose }) {
  const ref = useRef(null)
  const lang = findLanguage(current)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', onDoc, true)
    document.addEventListener('keydown', onEsc, true)
    return () => {
      document.removeEventListener('pointerdown', onDoc, true)
      document.removeEventListener('keydown', onEsc, true)
    }
  }, [open, onClose])

  return (
    <span ref={ref} className="pb-lang-pill" data-pb-skip-translate>
      <button
        type="button"
        className="pb-lang-trigger"
        onClick={onToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Choose language"
      >
        <span className="pb-lang-trigger-label">Language</span>
        {busy && (
          <span className="pb-lang-progress" aria-live="polite">
            {progress.total > 0 ? `${Math.round((progress.done / progress.total) * 100)}%` : '…'}
          </span>
        )}
        <svg className="pb-lang-caret" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <span className="pb-lang-menu" role="listbox" data-pb-skip-translate>
          {VISIBLE_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              className={`pb-lang-item ${l.code === current ? 'is-active' : ''}`}
              role="option"
              aria-selected={l.code === current}
              onClick={() => onPick(l.code)}
            >
              <span className="pb-lang-item-native">{l.native}</span>
              <span className="pb-lang-item-name">{l.name}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  )
}

const globalStyles = `
.lang-toggle:has(.pb-lang-pill) { background: transparent !important; border: 0 !important; box-shadow: none !important; padding: 0 !important; }
.pb-lang-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  font-family: 'Open Sans', system-ui, -apple-system, Segoe UI, sans-serif;
}
.pb-lang-trigger {
  appearance: none; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  font: 600 11px/1 'Open Sans', system-ui, sans-serif;
  letter-spacing: 0.04em;
  color: #000000; /* the pill background is white: always black text */
  padding: 5px 12px;
  border-radius: 999px;
  background: #ffffff; /* white pill in both themes, so the label is always black-on-white */
  border: 0;
  outline: 0;
  /* Full opacity on all three text runs (was 0.9 / 0.8 / 0.7). Dimming text
     with opacity is what made "EN / English" read grey in light mode and
     half-white in dark - the site-wide rule is pure #000 / pure #FFF. */
  opacity: 1;
  transition: opacity 140ms ease, color 140ms ease;
}
.pb-lang-trigger:hover { opacity: 1; }
.pb-lang-trigger:focus-visible { outline: 2px solid rgba(15, 79, 186, 0.40); outline-offset: 2px; }
.pb-lang-trigger-label { font-weight: 700; color: #000000; }
.pb-lang-caret { color: #000000; }
.pb-lang-trigger-native { font-weight: 500; opacity: 1; }
.pb-lang-progress { font-weight: 600; font-size: 10px; opacity: 1; }
.pb-lang-caret { opacity: 0.7; }
.pb-lang-menu {
  position: absolute; bottom: calc(100% + 8px); right: 0;
  /* Size to the widest row (e.g. "简体中文 … Chinese (Simplified)") so no
     cell has to wrap; clamp on small screens. */
  min-width: 240px; width: max-content; max-width: min(340px, 92vw);
  max-height: 320px; overflow-y: auto; overflow-x: hidden;
  padding: 6px;
  background: rgba(255,255,255,0.99);
  color: #000000;
  border: 1px solid rgba(15, 79, 186, 0.18);
  box-shadow: 0 16px 40px rgba(11,16,32,0.22), 0 2px 6px rgba(11,16,32,0.08);
  border-radius: 12px;
  z-index: 2147483645;
  display: flex; flex-direction: column; gap: 2px;
  animation: pb-lang-in 140ms ease-out;
}
:root[data-theme="dark"] .pb-lang-menu {
  background: rgba(14, 20, 38, 0.98);
  color: #FFFFFF;
  border-color: rgba(255,255,255,0.10);
}
@keyframes pb-lang-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.pb-lang-item {
  appearance: none; background: transparent; border: 0; cursor: pointer;
  display: flex; align-items: baseline; justify-content: space-between; gap: 16px;
  width: 100%; padding: 8px 10px; border-radius: 8px;
  color: inherit; text-align: left; font: 500 13px/1.2 'Open Sans', system-ui, sans-serif;
}
.pb-lang-item:hover { background: rgba(15, 79, 186, 0.10); }
:root[data-theme="dark"] .pb-lang-item:hover { background: rgba(255,255,255,0.08); }
.pb-lang-item.is-active { background: rgba(15, 79, 186, 0.14); font-weight: 700; }
:root[data-theme="dark"] .pb-lang-item.is-active { background: rgba(255,255,255,0.10); }
.pb-lang-item-native { font-weight: 700; white-space: nowrap; flex: 0 0 auto; }
.pb-lang-item-name { font-size: 11px; opacity: 1; white-space: nowrap; flex: 0 0 auto; text-align: right; }
`
